import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";

const EstimatePage = () => {
  const { id } = useParams();
  const [estimateData, setEstimateData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [grage_name, setGrage_name] = useState("");
  const [grage_upi_id, setGrage_upi_id] = useState("");
  const [grage_id, setGrage_id] = useState("");
  const [garageTerms, setGarageTerms] = useState([]); // NEW: Store garage T&C
  const contentRef = useRef(null);
  const server_url = localStorage.getItem("server_url");
  const server_folder = localStorage.getItem("server_folder");

  const formatAmount = (amount) => {
    if (!amount) return "₹0";
    return `₹${parseInt(amount).toLocaleString("en-IN")}`;
  };

  useEffect(() => {
    fetchEstimateData();
  }, [id]);

  useEffect(() => {
    if (grage_id) {
      fetchUserData();
      fetchGarageTerms(); // NEW: Fetch garage terms when grage_id is available
    }
  }, [grage_id]);

  // NEW: Function to fetch garage terms and conditions
  const fetchGarageTerms = async () => {
    try {
      const formData = new FormData();
      formData.append("type", 1);
      formData.append("garage_id", grage_id);

      const response = await axios.post(
        `${server_url}garage_tc.php`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      if (response.data.status == "1") {
        setGarageTerms(response.data.data || []);
      }
    } catch (err) {
      console.error("Error fetching garage terms:", err);
    }
  };

  const fetchUserData = async () => {
    try {
      const form = new FormData();
      form.append("company_id", 2);
      form.append("garage_id", grage_id);
      form.append("version_code", "1.0");

      const response = await axios.post(`${server_url}check_user.php`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (
        response.data.status === "1" &&
        response.data.result &&
        response.data.result.length > 0
      ) {
        const user = response.data.result[0];
        setGrage_name(user.grage_name || "");
        setGrage_upi_id(user.grage_upi_id || "");
      }
    } catch (err) {
      console.error("check_user error:", err);
    }
  };

  const fetchEstimateData = async () => {
    try {
      const formData = new FormData();
      formData.append("vehicle_entry_id", id);
      formData.append("company_id", "2");

      const response = await axios.post(
        `${server_url}estimate_details.php`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      if (response.data.status === "1") {
        setGrage_id(response.data.result.grage_id || "");
        setEstimateData(response.data.result);
      } else {
        setError(response.data.error || "Estimate not found");
      }
    } catch (err) {
      console.error("Fetch estimate error:", err);
      setError("Failed to load estimate");
    } finally {
      setLoading(false);
    }
  };

  const calculateTotals = (items = []) => {
    let subtotal = 0;
    let totalGST = 0;

    items.forEach((item) => {
      const rate = parseFloat(item.vehicle_entry_detail_rate) || 0;
      const qty = parseFloat(item.vehicle_entry_detail_qty) || 0;
      const gst = parseFloat(item.vehicle_entry_detail_gst) || 0;

      const itemTotal = rate * qty;
      const itemGST = (itemTotal * gst) / 100;

      subtotal += itemTotal;
      totalGST += itemGST;
    });

    const finalTotal = subtotal + totalGST;

    return {
      subtotal: subtotal.toFixed(2),
      totalGST: totalGST.toFixed(2),
      finalTotal: finalTotal.toFixed(2),
    };
  };

  const handleDownloadPDF = async () => {
    try {
      setDownloading(true);

      // Check if 7 or more products
      const productCount = estimateData?.service_items?.length || 0;
      const needsSecondPage = productCount >= 7;

      // Create a clone of the content for PDF generation
      const content = contentRef.current;
      const pdfClone = content.cloneNode(true);

      // Add desktop-pdf class for proper layout in PDF
      pdfClone.classList.add("desktop-pdf");

      // Hide the entire estimate-footer from first page if we need second page
      const estimateFooter = pdfClone.querySelector(".estimate-footer");
      const termsSection = pdfClone.querySelector(".terms-section"); // NEW: Get terms section
      const developerCredit = pdfClone.querySelector(".developer-credit"); // NEW: Get developer credit

      if (needsSecondPage && estimateFooter) {
        estimateFooter.style.display = "none";
      }
      if (needsSecondPage && termsSection) {
        termsSection.style.display = "none";
      }
      if (needsSecondPage && developerCredit) {
        developerCredit.style.display = "none";
      }

      // Apply PDF-specific styles
      pdfClone.style.width = "800px";
      pdfClone.style.maxWidth = "800px";
      pdfClone.style.margin = "0 auto";

      // Temporarily append to body for rendering
      const tempContainer = document.createElement("div");
      tempContainer.style.position = "absolute";
      tempContainer.style.left = "-9999px";
      tempContainer.style.top = "0";
      tempContainer.appendChild(pdfClone);
      document.body.appendChild(tempContainer);

      // Create PDF
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
        compress: true,
      });

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      // Create canvas for first page (without footer, terms, developer credit)
      const canvas = await html2canvas(pdfClone, {
        scale: 2,
        useCORS: true,
        logging: false,
        allowTaint: true,
        backgroundColor: "#ffffff",
        onclone: (clonedDoc) => {
          // Hide footer, terms, and developer credit in first page if needed
          if (needsSecondPage) {
            const clonedFooter = clonedDoc.querySelector(".estimate-footer");
            const clonedTerms = clonedDoc.querySelector(".terms-section");
            const clonedDeveloper =
              clonedDoc.querySelector(".developer-credit");

            if (clonedFooter) {
              clonedFooter.style.display = "none";
            }
            if (clonedTerms) {
              clonedTerms.style.display = "none";
            }
            if (clonedDeveloper) {
              clonedDeveloper.style.display = "none";
            }
          }

          // Add print-specific styles
          const style = clonedDoc.createElement("style");
          style.textContent = `
          @media print {
            .mobile-action-container { display: none !important; }
            .estimate-footer, 
            .terms-section,
            .developer-credit { 
              display: block !important;
              page-break-inside: avoid !important;
            }
            .qr-container { flex-direction: row !important; }
          }
        `;
          clonedDoc.head.appendChild(style);
        },
      });

      // Clean up temporary container
      document.body.removeChild(tempContainer);

      // Calculate dimensions for first page
      const imgWidth = pdfWidth - 20; // 10mm margins on each side
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      // Add first page image to PDF (without footer, terms, developer credit)
      const imgData = canvas.toDataURL("image/jpeg", 1.0);
      pdf.addImage(imgData, "JPEG", 10, 10, imgWidth, imgHeight);

      // If 7 or more products, create second page with the remaining content
      if (needsSecondPage) {
        pdf.addPage();

        // Get the original content sections
        const originalFooter =
          contentRef.current.querySelector(".estimate-footer");
        const originalTerms =
          contentRef.current.querySelector(".terms-section");
        const originalDeveloper =
          contentRef.current.querySelector(".developer-credit");

        // Create second page with the remaining content
        const secondPageContent = document.createElement("div");
        secondPageContent.className = "second-page-content";
        secondPageContent.style.width = "800px";
        secondPageContent.style.padding = "20px";
        secondPageContent.style.backgroundColor = "#ffffff";
        secondPageContent.style.marginTop = "20px";

        // Clone and append footer if it exists
        if (originalFooter) {
          const footerClone = originalFooter.cloneNode(true);
          footerClone.style.display = "block";
          footerClone.style.marginTop = "0";
          footerClone.style.padding = "15px";
          secondPageContent.appendChild(footerClone);
        }

        // Clone and append terms section if it exists
        if (originalTerms && garageTerms.length > 0) {
          const termsClone = originalTerms.cloneNode(true);
          termsClone.style.display = "block";
          termsClone.style.marginTop = "15px";
          secondPageContent.appendChild(termsClone);
        }

        // Clone and append developer credit if it exists
        if (originalDeveloper) {
          const developerClone = originalDeveloper.cloneNode(true);
          developerClone.style.display = "block";
          developerClone.style.marginTop = "15px";
          developerClone.style.textAlign = "center";
          secondPageContent.appendChild(developerClone);
        }

        // Add page indicator at the bottom
        const pageIndicator = document.createElement("div");
        pageIndicator.innerHTML = `
        `;
        secondPageContent.appendChild(pageIndicator);

        // Render second page
        const secondTempContainer = document.createElement("div");
        secondTempContainer.style.position = "absolute";
        secondTempContainer.style.left = "-9999px";
        secondTempContainer.style.top = "0";
        secondTempContainer.appendChild(secondPageContent);
        document.body.appendChild(secondTempContainer);

        const canvas2 = await html2canvas(secondPageContent, {
          scale: 2,
          useCORS: true,
          logging: false,
          allowTaint: true,
          backgroundColor: "#ffffff",
        });

        document.body.removeChild(secondTempContainer);

        const imgData2 = canvas2.toDataURL("image/jpeg", 1.0);
        const imgHeight2 = (canvas2.height * imgWidth) / canvas2.width;

        // Add second page to PDF
        pdf.addImage(imgData2, "JPEG", 10, 10, imgWidth, imgHeight2);
      }

      // Add page numbers
      const totalPages = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(10);
        pdf.setTextColor(100, 100, 100);
        pdf.text(`Page ${i} of ${totalPages}`, pdfWidth - 25, pdfHeight - 10);
      }

      // Save PDF
      const payeeName = grage_name || "MydigiGarage";

      const fileName =
        payeeName +
        ` Estimate #${estimateData.vehicle_entry_no} ${
          new Date().toISOString().split("T")[0]
        }.pdf`;
      pdf.save(fileName);

      setDownloading(false);
    } catch (err) {
      console.error("PDF generation error:", err);
      setDownloading(false);
      alert("Failed to generate PDF. Please try again.");
    }
  };

  const handleUPIPayment = () => {
    if (!grage_upi_id) {
      alert("UPI ID not configured. Please contact support.");
      return;
    }
    const totals = calculateTotals(estimateData?.service_items);
    const amount = parseFloat(totals.finalTotal).toFixed(2); // Ensure 2 decimals
    const payeeName = encodeURIComponent(grage_name || "Garage");
    const transactionNote = encodeURIComponent(
      grage_name + ` #${estimateData.vehicle_entry_no}`
    ); // Add note for better tracking
    const upiURL = `upi://pay?pa=${grage_upi_id}&pn=${payeeName}&am=${amount}&cu=INR&tn=${transactionNote}`;

    // Track if app opened (optional: use a flag)
    const startTime = Date.now();
    window.location.href = upiURL;

    // Enhanced fallback after 2s (increased timeout)
    setTimeout(() => {
      const timeDiff = Date.now() - startTime;
      if (timeDiff < 2000) {
        // App likely opened
        // Optional: Show a tip modal after payment attempt
        setTimeout(() => {
          if (
            window.confirm(
              "Payment app opened? If you see a 'no account registered' error, add a bank to your UPI app first.\n\nShow QR code instead?"
            )
          ) {
            setShowQR(true);
          }
        }, 3000);
      } else {
        setShowQR(true);
      }
    }, 2000);
  };

  const handleCloseQR = () => {
    setShowQR(false);
  };

  if (loading) {
    return (
      <div className="mobile-estimate-loading">
        <div className="mobile-loading-spinner"></div>
        <p>Loading your estimate...</p>
      </div>
    );
  }

  if (error || !estimateData) {
    return (
      <div className="mobile-estimate-error">
        <div className="mobile-error-icon">⚠️</div>
        <h3>Estimate Not Found</h3>
        <p>{error || "The estimate you're looking for doesn't exist."}</p>
        <button onClick={() => window.close()} className="mobile-close-btn">
          Close Window
        </button>
      </div>
    );
  }

  const totals = calculateTotals(estimateData.service_items);

  return (
    <div className="mobile-estimate-wrapper">
      {/* Main Content */}
      <div
        id="estimate-full-content"
        ref={contentRef}
        className="estimate-content"
      >
        {/* Header */}
        <div className="header-right" style={{ padding: "5px 15px" }}>
          <div className="estimate-info">
            <div className="estimate-meta">
              <div className="meta-item">
                <span
                  className="meta-label"
                  style={{ color: "#00409e" }}
                ></span>
                <span className="meta-value">
                  <img
                    src={server_folder + "/img/2.png"}
                    alt="Garage Logo"
                    style={{ height: "24px" }}
                  />
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className="estimate-header">
          <div className="header-left">
            <h1 className="garage-name">
              {estimateData.grage_name || "Auto Care Center"}
            </h1>
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                estimateData.grage_address || ""
              )}`}
              target="_blank"
              rel="noopener noreferrer"
              className="garage-address"
            >
              {estimateData.grage_address || "Professional Auto Services"}
            </a>
            {estimateData.grage_mobile ? (
              <a
                href={`tel:${estimateData.grage_mobile}`}
                className="garage-contact"
              >
                <span className="contact-icon">📞</span>
                {estimateData.grage_mobile}
              </a>
            ) : (
              <p className="garage-contact">Contact Us</p>
            )}
          </div>

          <div className="header-right">
            <div className="estimate-info">
              <h2 className="estimate-title">SERVICE ESTIMATE</h2>
              <div className="estimate-meta">
                <div className="meta-item">
                  <span className="meta-label">
                    Estimate #:{" "}
                    <span className="meta-value">
                      {estimateData.vehicle_entry_no}
                    </span>
                  </span>
                  <span className="meta-value">
                    {estimateData.vehicle_entry_in_date}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Customer & Vehicle Info */}
        <div className="info-section">
          <div className="info-card customer-info">
            <div className="card-header" style={{ textAlign: "left" }}>
              <h3>👤 Customer Info</h3>
            </div>
            <div className="card-body">
              <div className="info-row">
                <span className="info-label">Name:</span>
                <span className="info-value" style={{ textAlign: "right" }}>
                  {estimateData.customer_name || "N/A"}
                </span>
              </div>
              <div className="info-row">
                <span className="info-label">Mobile:</span>
                <span
                  className="info-value highlight"
                  style={{ textAlign: "right" }}
                >
                  {estimateData.customer_mobile ? (
                    <a
                      href={`tel:${estimateData.customer_mobile}`}
                      style={{ color: "#0352a1", textDecoration: "none" }}
                    >
                      {estimateData.customer_mobile}
                    </a>
                  ) : (
                    "N/A"
                  )}
                </span>
              </div>
              <div className="info-row">
                <span className="info-label">Address:</span>
                <span className="info-value" style={{ textAlign: "right" }}>
                  {estimateData.customer_address ? (
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                        estimateData.customer_address
                      )}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: "#0352a1",
                        textDecoration: "none",
                        whiteSpace: "pre-line",
                      }}
                    >
                      {estimateData.customer_address
                        .split(",")
                        .map((part) => part.trim())
                        .join("\n")}{" "}
                    </a>
                  ) : (
                    "N/A"
                  )}
                </span>
              </div>
            </div>
          </div>

          <div className="info-card vehicle-info">
            <div className="card-header" style={{ textAlign: "left" }}>
              <h3>🚗 Vehicle Info</h3>
            </div>
            <div className="card-body">
              <div className="info-row">
                <span className="info-label">Reg No:</span>
                <span className="info-value highlight">
                  {estimateData.vehicle_reg_no || "N/A"}
                </span>
              </div>
              <div className="info-row">
                <span className="info-label">Model:</span>
                <span className="info-value">
                  {estimateData.model_name || "N/A"}
                </span>
              </div>
              <div className="info-row">
                <span className="info-label">Brand:</span>
                <span className="info-value">
                  {estimateData.brand_name || "N/A"}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Service Items */}
        <div className="items-section">
          <div className="section-header">
            <span style={{ marginBottom: "10px" }} className="section-icon">
              🔧
            </span>
            <h3>Service Items & Parts</h3>
          </div>

          <div className="table-container">
            <table className="items-table">
              <thead>
                <tr>
                  <th style={{ textAlign: "left" }} className="col-description">
                    Description
                  </th>
                  <th style={{ textAlign: "center" }} className="col-qty">
                    Qty
                  </th>
                  <th style={{ textAlign: "right" }} className="col-rate">
                    Rate
                  </th>
                  <th style={{ textAlign: "right" }} className="col-gst">
                    GST
                  </th>
                  <th style={{ textAlign: "right" }} className="col-amount">
                    Amount
                  </th>
                </tr>
              </thead>
              <tbody>
                {estimateData.service_items &&
                estimateData.service_items.length > 0 ? (
                  estimateData.service_items.map((item, index) => (
                    <tr
                      key={index}
                      className={index % 2 === 0 ? "even-row" : "odd-row"}
                    >
                      <td
                        style={{ textAlign: "left" }}
                        className="col-description"
                      >
                        {item.vehicle_entry_detail_description}
                      </td>
                      <td style={{ textAlign: "center" }} className="col-qty">
                        {item.vehicle_entry_detail_qty}
                      </td>
                      <td style={{ textAlign: "right" }} className="col-rate">
                        {item.vehicle_entry_detail_rate > 0 ? (
                          <>{formatAmount(item.vehicle_entry_detail_rate)}</>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td style={{ textAlign: "right" }} className="col-gst">
                        {item.vehicle_entry_detail_gst > 0
                          ? item.vehicle_entry_detail_gst + "%"
                          : "-"}
                      </td>
                      <td style={{ textAlign: "right" }} className="col-amount">
                        {item.vehicle_entry_detail_amt > 0 ? (
                          <>{formatAmount(item.vehicle_entry_detail_amt)}</>
                        ) : (
                          <>
                            <span style={{ color: "#34A853" }}>Free</span>
                          </>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr className="no-items-row">
                    <td colSpan="5" className="no-items">
                      <div className="no-items-content">
                        <span className="no-items-icon">📝</span>
                        <p>No service items added yet</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div className="totals-section">
            <div className="total-row">
              <span className="total-label">Subtotal:</span>
              <span className="total-value">
                {formatAmount(totals.subtotal)}
              </span>
            </div>
            {totals.totalGST > 0 && (
              <div className="total-row">
                <span className="total-label">GST:</span>
                <span className="total-value">
                  {formatAmount(totals.totalGST)}
                </span>
              </div>
            )}
            <div className="total-row grand-total">
              <span className="total-label">Grand Total:</span>
              <span className="total-value">
                {formatAmount(totals.finalTotal)}
              </span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="estimate-footer">
          <div className="footer-grid">
            <div className="approval-section">
              <div className="approval-card">
                <div className="approval-header">
                  <h4>For Customer</h4>
                </div>
                <div className="approval-body">
                  <div className="signature-area">
                    <div className="signature-line"></div>
                    <span className="signature-label">Name & Signature</span>
                  </div>
                  <div className="approval-note">
                    <p>I approve the above estimate and authorize the work.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="thank-you-section">
              <div className="thank-you-card">
                <div className="thank-you-icon">🙏</div>
                <h4>Thank You!</h4>
                <p>Thank you for choosing our services.</p>
                <div className="contact-info">
                  <p style={{ marginBottom: "0px" }}>
                    For queries:{" "}
                    <span className="contact-highlight">
                      {estimateData.grage_mobile ? (
                        <a
                          href={`tel:${estimateData.grage_mobile}`}
                          style={{ color: "#0352a1", textDecoration: "none" }}
                        >
                          {estimateData.grage_mobile}
                        </a>
                      ) : (
                        "Service Center"
                      )}
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* UPI Payment Section (in PDF) */}
          {grage_upi_id && (
            <div className="upi-section">
              <div className="upi-card">
                <div className="upi-header">
                  <h4>💳 Quick UPI Payment</h4>
                </div>
                <div className="upi-body">
                  <div className="qr-container">
                    <div className="qr-code">
                      <img
                        src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(
                          `upi://pay?pa=${grage_upi_id}&pn=${encodeURIComponent(
                            grage_name || "Garage"
                          )}&am=${totals.finalTotal}&cu=INR`
                        )}`}
                        alt="UPI QR Code"
                        className="qr-image"
                      />
                    </div>
                    <div className="upi-details">
                      <p className="upi-amount">Amount: ₹{totals.finalTotal}</p>
                      <p className="upi-id">UPI ID: {grage_upi_id}</p>
                      <p className="upi-name">Name: {grage_name}</p>
                      <p className="upi-instruction">
                        Scan & Pay with any UPI App
                      </p>
                      <p className="upi-note">
                        Ensure your UPI app has a linked bank account.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        {/* NEW: Terms and Conditions Section */}
        {garageTerms.length > 0 && (
          <div className="terms-section">
            <div className="section-header">
              <span className="section-icon">📋</span>
              <h3>Terms & Conditions</h3>
            </div>

            <div className="terms-content">
              <div className="terms-card">
                <div className="terms-list">
                  <ul>
                    {garageTerms.map((term, index) => (
                      <li key={term.garage_tc_id || index}>
                        {term.garage_tc_title}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* NEW: Developer Credit */}
        <div className="developer-credit">
          <p className="credit-text">
            This estimate is generated for free by{" "}
            <span className="company-name">MydigiGarage.</span>
          </p>
        </div>
      </div>

      {/* Floating Action Buttons */}
      <div className="mobile-action-container">
        <button
          onClick={handleDownloadPDF}
          className="mobile-action-btn download-btn"
          disabled={downloading}
        >
          <span className="action-icon">{downloading ? "⏳" : "📥"}</span>
          {downloading ? "Downloading..." : "Download PDF"}
        </button>

        <button
          onClick={handleUPIPayment}
          className="mobile-action-btn payment-btn"
          disabled={!grage_upi_id}
        >
          <span className="action-icon">💳</span>
          Pay Now
        </button>
      </div>

      {/* QR Modal */}
      {showQR && (
        <div className="qr-modal-overlay">
          <div className="qr-modal">
            <div className="qr-modal-header">
              <h3>Pay with UPI</h3>
              <button onClick={handleCloseQR} className="close-modal-btn">
                ×
              </button>
            </div>
            <div className="qr-modal-body">
              <div className="qr-container">
                <img
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(
                    `upi://pay?pa=${grage_upi_id}&pn=${encodeURIComponent(
                      grage_name || "Garage"
                    )}&am=${totals.finalTotal}&cu=INR`
                  )}`}
                  alt="UPI QR Code"
                  className="qr-image"
                />
                <div className="payment-details">
                  <p className="payment-amount">Amount: ₹{totals.finalTotal}</p>
                  <p className="payment-upi">UPI ID: {grage_upi_id}</p>
                  <p className="payment-name">Name: {grage_name}</p>
                  <p className="payment-instruction">
                    Scan this QR code with any UPI app
                  </p>
                  <p className="payment-note">
                    Ensure your UPI app has a linked bank account.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        /* Add to your existing CSS */
        .second-page-content {
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
            sans-serif;
          background: white;
        }

        .second-page-content .estimate-footer {
          display: block !important;
          margin-top: 0 !important;
          padding: 15px !important;
          background: #f8faff !important;
          border-top: 1px solid #e8f4ff !important;
          border-radius: 8px !important;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05) !important;
        }

        .second-page-content .terms-section {
          display: block !important;
          margin-top: 15px !important;
          padding: 15px !important;
        }

        .second-page-content .developer-credit {
          display: block !important;
          margin-top: 15px !important;
          text-align: center !important;
        }

        /* Ensure proper display of footer sections in second page */
        .second-page-content .footer-grid {
          display: flex !important;
          flex-direction: row !important;
          gap: 15px !important;
          margin-bottom: 15px !important;
        }

        .second-page-content .approval-section,
        .second-page-content .thank-you-section {
          flex: 1 !important;
        }

        .second-page-content .upi-section {
          margin-top: 15px !important;
          display: block !important;
        }

        /* Make sure footer content looks good on second page */
        .second-page-content .approval-card {
          height: 100% !important;
          min-height: 200px !important;
        }

        .second-page-content .thank-you-card {
          height: 100% !important;
          min-height: 200px !important;
          display: flex !important;
          flex-direction: column !important;
          justify-content: center !important;
        }

        /* NEW: Terms and Conditions Styles */
        .terms-section {
          margin-top: 0px;
          padding: 10px 20px;
        }

        .terms-content {
          margin-top: 10px;
        }

        .terms-card {
          border-radius: 6px;
        }

        .terms-list ul {
          margin: 0;
          padding-left: 20px;
          list-style-type: disc;
        }

        .terms-list li {
          margin-bottom: 8px;
          padding-left: 5px;
          color: #333;
          font-size: 14px;
          line-height: 1.4;
        }

        .terms-list li:last-child {
          margin-bottom: 0;
        }

        /* NEW: Developer Credit Styles */
        .developer-credit {
          padding: 10px;
          text-align: right;
          color: #666;
          font-size: 12px;
          border-top: 1px dashed #ddd;
        }

        .credit-text {
          margin: 0;
          font-style: italic;
        }

        .company-name {
          font-weight: 600;
          color: #0352a1;
        }

        /* Print styles for multi-page */
        @media print {
          .estimate-footer,
          .terms-section,
          .developer-credit {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }

          /* Ensure UPI QR is visible */
          .upi-section {
            display: block !important;
          }
        }

        /* PDF-specific styles */
        .pdf-export .upi-section {
          margin-top: 5px !important; /* Reduced */
          padding: 8px !important;
          border: 2px solid #28a745 !important;
          border-radius: 8px !important;
          background: #f8fff8 !important;
          page-break-inside: avoid !important;
          break-inside: avoid !important;
        }

        .pdf-export .qr-container {
          display: flex !important;
          flex-direction: row !important;
          justify-content: center !important;
          align-items: center !important;
          gap: 10px !important;
          padding: 5px !important;
        }

        .pdf-export .qr-code {
          flex-shrink: 0 !important;
          padding: 5px !important;
          background: white !important;
          border: 1px solid #ddd !important;
          border-radius: 4px !important;
        }

        .pdf-export .qr-image {
          width: 100px !important; /* Reduced from 120px for compactness */
          height: 100px !important;
          object-fit: contain !important;
        }

        .pdf-export .upi-details {
          text-align: left !important;
          flex-grow: 1 !important;
        }

        .pdf-export .upi-amount {
          font-size: 16px !important; /* Slightly reduced */
          font-weight: 700 !important;
          color: #28a745 !important;
          margin-bottom: 5px !important;
        }

        .pdf-export .upi-id,
        .pdf-export .upi-name {
          margin: 2px 0 !important;
          font-size: 12px !important;
        }

        .pdf-export .upi-instruction {
          margin: 5px 0 2px 0 !important;
          font-weight: 600 !important;
          color: #0352a1 !important;
        }

        .pdf-export .upi-note {
          margin: 2px 0 0 0 !important;
          font-size: 10px !important;
          color: #666 !important;
          font-style: italic !important;
        }

        /* Ensure sections don't break */
        .pdf-export .estimate-footer,
        .pdf-export .terms-section,
        .pdf-export .developer-credit {
          page-break-before: avoid !important;
          page-break-inside: avoid !important;
          break-inside: avoid !important;
          margin-bottom: 0 !important;
        }

        /* Force UPI section to new page if needed */
        @media print {
          .upi-section {
            page-break-before: auto !important;
            page-break-inside: avoid !important;
            page-break-after: avoid !important;
          }
        }

        /* Alternative: If content is too long, make QR section more compact */
        @media screen and (max-height: 800px) {
          .qr-container {
            gap: 8px !important;
          }

          .qr-image {
            width: 80px !important;
            height: 80px !important;
          }

          .upi-details {
            font-size: 11px !important;
          }
        }

        /* Base Styles */
        .h1,
        .h2,
        .h3,
        .h4,
        .h5,
        .h6,
        h1,
        h2,
        h3,
        h4,
        h5,
        h6 {
          margin-bottom: 0rem;
        }

        .mobile-estimate-wrapper {
          min-height: 100vh;
          background: #f8f9fa;
          padding: 16px;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
            sans-serif;
          position: relative;
          padding-bottom: 100px; /* Space for floating buttons */
        }

        .estimate-content {
          max-width: 900px;
          margin: 0 auto;
          background: white;
          border-radius: 12px;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
          overflow: hidden;
        }

        /* Force desktop for PDF clone */
        .desktop-pdf {
          width: 800px !important;
        }

        .desktop-pdf .info-section {
          flex-direction: row !important;
        }

        .desktop-pdf .info-card.customer-info,
        .desktop-pdf .info-card.vehicle-info {
          width: calc(50% - 8px) !important;
        }

        .desktop-pdf .footer-grid {
          flex-direction: row !important;
        }

        .desktop-pdf .estimate-header {
          flex-direction: row !important;
          justify-content: space-between !important;
          align-items: flex-start !important;
        }

        .desktop-pdf .header-left {
          text-align: left !important;
        }

        .desktop-pdf .header-right {
          text-align: right !important;
        }

        .desktop-pdf .garage-contact {
          justify-content: flex-start !important;
        }

        .desktop-pdf .qr-container {
          flex-direction: row !important;
          justify-content: center !important;
          text-align: left !important;
        }

        .desktop-pdf .mobile-action-container {
          flex-wrap: nowrap !important;
        }

        .desktop-pdf .terms-section,
        .desktop-pdf .developer-credit {
          display: block !important;
        }

        /* Floating Action Buttons */
        .mobile-action-container {
          position: fixed;
          bottom: 0;
          left: 0;
          right: 0;
          display: flex;
          justify-content: center;
          gap: 12px;
          padding: 16px;
          z-index: 1000;
          flex-wrap: wrap;
        }

        .mobile-action-btn {
          border: none;
          padding: 10px;
          border-radius: 8px;
          font-weight: 600;
          font-size: 14px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: all 0.3s ease;
          min-width: 140px;
          justify-content: center;
        }

        .download-btn {
          background: #0352a1;
          color: white;
        }

        .download-btn:hover:not(:disabled) {
          background: #023e7d;
        }

        .payment-btn {
          background: #28a745;
          color: white;
        }

        .payment-btn:hover:not(:disabled) {
          background: #218838;
        }

        .mobile-action-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        /* QR Modal */
        .qr-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.7);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1000;
          padding: 10px;
        }

        .qr-modal {
          background: white;
          border-radius: 12px;
          padding: 0;
          max-width: 400px;
          width: 100%;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
          animation: modalAppear 0.3s ease;
        }

        @keyframes modalAppear {
          from {
            opacity: 0;
            transform: scale(0.9);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        .qr-modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 10px;
          border-bottom: 1px solid #e8f4ff;
          background: #0352a1;
          color: white;
          border-radius: 12px 12px 0 0;
        }

        .qr-modal-header h3 {
          margin: 0;
          font-size: 18px;
        }

        .close-modal-btn {
          background: none;
          border: none;
          color: white;
          font-size: 24px;
          cursor: pointer;
          padding: 0;
          width: 30px;
          height: 30px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .qr-modal-body {
          padding: 30px;
          text-align: center;
        }

        .qr-image {
          width: 150px;
          height: 150px;
          border: 1px solid #e8f4ff;
          border-radius: 8px;
          padding: 10px;
          background: white;
        }

        .payment-details {
          text-align: center;
        }

        .payment-amount {
          font-size: 10px;
          font-weight: 700;
          color: #28a745;
          margin: 0 0 10px 0;
        }

        .payment-upi,
        .payment-name {
          margin: 8px 0;
          color: #333;
          font-weight: 500;
        }

        .payment-instruction {
          margin: 16px 0 0 0;
          color: #666;
          font-size: 14px;
        }

        .payment-note {
          margin: 8px 0 0 0;
          color: #ff6b35;
          font-size: 14px;
          font-weight: 500;
        }

        /* Estimate Header */
        .estimate-header {
          background: white;
          padding: 10px;
          border-bottom: 3px solid #0352a1;
          display: flex;
          flex-direction: column;
          gap: 0px;
        }

        .header-left {
          text-align: center;
        }

        .garage-name {
          margin: 0 0 8px 0;
          color: #0352a1;
          font-size: 24px;
          font-weight: 700;
        }

        .garage-address {
          margin: 4px 0;
          color: #666;
          font-size: 14px;
          text-decoration: none;
          color: #0352a1;
        }

        .garage-contact {
          margin: 4px 0;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          font-weight: 600;
          color: #0352a1;
          text-decoration: none;
        }

        .header-right {
          text-align: center;
        }

        .estimate-title {
          margin: 0 0 8px 0;
          color: #0352a1;
          font-size: 20px;
          font-weight: 700;
        }

        .estimate-meta {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .meta-item {
          display: flex;
          justify-content: space-between;
          gap: 8px;
        }

        .meta-label {
          color: #666;
          font-weight: 500;
        }

        .meta-value {
          font-weight: 600;
          color: #0352a1;
        }

        /* Info Section */
        .info-section {
          display: -webkit-box;
          flex-direction: column;
          gap: 10px;
          padding: 16px 17px 0px 7px;
          font-size: 11px;
        }

        .info-card.customer-info,
        .info-card.vehicle-info {
          width: 50%;
        }

        .info-card {
          border: 1px solid #e0e6ff;
          border-radius: 8px;
          overflow: hidden;
          background: white;
        }

        .card-header {
          background: #f8faff;
          padding: 12px;
          border-bottom: 1px solid #e8f4ff;
          align-items: center;
          gap: 8px;
        }

        .card-header h3 {
          margin: 0;
          color: #0352a1;
          font-size: 16px;
          font-weight: 600;
        }

        .card-body {
          padding: 16px;
        }

        .info-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 7px 0;
          border-bottom: 1px solid #f0f4f8;
        }

        .info-row:last-child {
          border-bottom: none;
        }

        .info-label {
          color: #666;
          font-weight: 500;
        }

        .info-value {
          font-weight: 600;
          color: #333;
        }

        .highlight {
          color: #0352a1;
        }

        /* Items Section */
        .items-section {
          padding: 10px;
        }

        .section-header {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 5px;
        }

        .section-header h3 {
          margin: 0;
          margin-bottom: 5px;
          color: #0352a1;
          font-size: 18px;
          font-weight: 600;
        }

        .table-container {
          border: 1px solid #e8f4ff;
          border-radius: 8px;
          overflow: visible;
        }

        .items-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 14px;
          table-layout: auto;
        }

        .items-table th {
          background: #0352a1;
          color: white;
          padding: 12px 8px;
          font-weight: 600;
          text-align: left;
        }

        .items-table td {
          padding: 12px 8px;
          border-bottom: 1px solid #f0f4f8;
          vertical-align: top;
        }

        .even-row {
          background: #fafbff;
        }

        .odd-row {
          background: white;
        }

        .col-description {
          width: 40%;
          word-wrap: break-word;
          overflow-wrap: break-word;
          hyphens: auto;
          text-align: left;
        }
        .col-qty,
        .col-gst {
          width: 10%;
          text-align: center;
        }
        .col-rate,
        .col-amount {
          width: 20%;
          text-align: right;
        }

        /* Totals */
        .totals-section {
          border-top: 2px solid #0352a1;
          padding-top: 5px;
          margin-left: auto;
        }

        .total-row {
          display: flex;
          justify-content: space-between;
          padding: 6px 0;
          font-size: 14px;
        }

        .grand-total {
          font-weight: 700;
          color: #0352a1;
          font-size: 16px;
          border-top: 1px solid #ddd;
          padding-top: 12px;
        }

        /* Footer */
        .estimate-footer {
          padding: 10px;
          background: #f8faff;
          border-top: 1px solid #e8f4ff;
        }

        .footer-grid {
          display: flex;
          flex-direction: column;
          gap: 10px;
          margin-bottom: 10px;
        }

        .approval-section,
        .thank-you-section {
          flex: 1;
        }

        .approval-card,
        .thank-you-card {
          border: 1px solid #e8f4ff;
          border-radius: 8px;
          overflow: hidden;
          background: white;
        }

        .approval-header {
          background: #ffffffff;
          color: black;
          padding: 16px;
          text-align: center;
        }

        .approval-header h4 {
          margin: 0;
          font-size: 16px;
        }

        .approval-body {
          padding: 10px;
        }

        .signature-area {
          text-align: center;
          margin-bottom: 16px;
        }

        .signature-line {
          border-bottom: 1px solid #333;
          margin: 10px 0 8px;
        }

        .signature-label {
          color: #666;
          font-size: 12px;
        }

        .thank-you-card {
          padding: 10px;
          text-align: center;
        }

        .thank-you-icon {
          font-size: 32px;
          margin-bottom: 12px;
        }

        .contact-highlight {
          color: #0352a1;
          font-weight: 700;
        }

        /* UPI Styles */
        .upi-section {
          margin-top: 10px;
        }

        .upi-card {
          border: 2px solid #e8f4ff;
          border-radius: 8px;
          overflow: hidden;
          background: white;
        }

        .upi-header {
          background: #28a745;
          color: white;
          padding: 8px;
          text-align: center;
        }

        .upi-body {
          padding: 10px;
        }

        .qr-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 16px;
          text-align: center;
        }

        .upi-details {
          text-align: left;
        }

        .upi-amount {
          font-size: 18px;
          font-weight: 700;
          color: #28a745;
          margin: 0;
        }

        .upi-id,
        .upi-name {
          margin: 0px 0;
          color: #333;
          font-weight: 500;
        }

        .upi-instruction {
          margin: 0px 0 0 0;
          color: #666;
          font-size: 14px;
        }

        .upi-note {
          margin: 0px 0 0 0;
          color: #ff6b35;
          font-size: 12px;
          font-style: italic;
        }

        /* Tablet/Desktop Styles */
        @media (min-width: 768px) {
          .totals-section {
            max-width: 250px;
          }

          .mobile-estimate-wrapper {
            padding: 8px;
            padding-bottom: 70px;
          }

          .estimate-header {
            flex-direction: row;
            justify-content: space-between;
            align-items: flex-start;
          }

          .header-left {
            text-align: left;
          }

          .header-right {
            text-align: right;
          }

          .garage-contact {
            justify-content: flex-start;
          }

          .info-section {
            flex-direction: row;
            gap: 16px;
            font-size: 15px;
          }

          .info-card.customer-info,
          .info-card.vehicle-info {
            width: calc(50% - 8px);
          }

          .footer-grid {
            flex-direction: row;
          }

          .approval-section,
          .thank-you-section {
            flex: 1;
          }

          .qr-container {
            flex-direction: row;
            justify-content: center;
            text-align: left;
          }

          .upi-details {
            margin-left: 16px;
          }

          .mobile-action-container {
            flex-wrap: nowrap;
            padding: 10px;
          }
        }

        /* Mobile Optimizations */
        @media (max-width: 767px) {
          .mobile-estimate-wrapper {
            padding: 8px;
            padding-bottom: 70px;
          }

          .col-description {
            font-size: 13px;
            padding-right: 4px;
            width: 50%;
          }

          .col-qty {
            width: 8%;
          }

          .col-rate {
            width: 17%;
            text-align: right;
          }

          .col-gst {
            width: 8%;
          }

          .col-amount {
            width: 17%;
          }

          .items-table {
            font-size: 12px;
          }

          .items-table th,
          .items-table td {
            padding: 6px 4px;
          }

          .table-container {
            overflow-x: visible;
          }

          .qr-container {
            gap: 0px;
          }

          .upi-details {
            text-align: center;
          }

          .mobile-action-btn {
            min-width: calc(50% - 6px);
            flex: 1;
          }
        }

        /* Print Styles - Force desktop-like for print */
        @media print {
          .mobile-action-container,
          .qr-modal-overlay {
            display: none !important;
          }

          .mobile-estimate-wrapper {
            background: white !important;
            padding: 0 !important;
            padding-bottom: 0 !important;
          }

          .estimate-content {
            box-shadow: none !important;
            border-radius: 0 !important;
            max-width: none !important;
            width: 100% !important;
            overflow: visible !important;
          }

          .info-section,
          .footer-grid,
          .estimate-header {
            flex-direction: row !important;
          }

          .header-left {
            text-align: left !important;
          }

          .header-right {
            text-align: right !important;
          }

          .garage-contact {
            justify-content: flex-start !important;
          }

          .qr-container {
            flex-direction: row !important;
          }

          .table-container {
            overflow: visible !important;
            page-break-inside: avoid;
          }

          .items-table {
            table-layout: auto !important;
          }

          .info-card.customer-info,
          .info-card.vehicle-info {
            width: calc(50% - 8px) !important;
          }
        }
      `}</style>
    </div>
  );
};

export default EstimatePage;
